<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container class="py-5 custom-container container tradie-profile-view">
        <b-container class="custom-container mt-5">
          <b-breadcrumb
            :items="breadcrumbs"
            class="pl-0 pt-5 mt-5 bg-transparent"
          ></b-breadcrumb>

          <!-- ROW -->
          <b-row class="profile-header-row align-items-center">
            <b-col>
              <h2 class="font-weight-bold mb-md-0">
                {{
                  tradie ? (tradie.full_name ? tradie.full_name : '-----') : '-'
                }}
              </h2>
            </b-col>

            <b-col class="text-right align-right">
              <b-row>
                <b-col cols="12" sm="4" class="px-2"></b-col>
                <b-col cols="12" sm="4" class="px-2"></b-col>
                <b-col cols="12" sm="4" class="px-2">
                  <b-button
                    variant="outline-secondary"
                    class="bg-white text-dark"
                    :style="'max-height: 64px; max-width: 220px; width: 100%'"
                    @click="gotoTradies()"
                  >
                    {{
                      arrivedFromCandidateRedirect
                        ? 'Back to Candidates'
                        : 'Back to Tradies'
                    }}
                  </b-button>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <!-- Tradie has asked to delete their account (notice period) -->
          <b-alert
            v-if="accountDeletionDate"
            show
            variant="danger"
            class="mt-3"
            data-testid="tradie-account-deletion-notice"
          >
            This tradie is deleting their account and all their uploaded files.
            It will be removed on <strong>{{ accountDeletionDate }}</strong
            >. Keep a copy of anything you are required or permitted to retain.
          </b-alert>

          <!-- ROW - Details -->
          <b-row v-if="tradie">
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <b-container fluid>
                  <div
                    class="d-flex align-items-start mt-4 profile-detail-flex"
                    style="gap: 32px"
                  >
                    <!-- User Avatar -->
                    <image-cropper
                      v-model="showImageCropper"
                      @change="uploadProfilePhoto"
                      @close="showImageCropper = false"
                      subtitle="Upload Tradie's Profile Photo"
                      loadingMessage="Uploading Image"
                    />
                    <div
                      class="upload-container mb-4"
                      :class="tradie && tradie.avatar ? 'border-0' : ''"
                      v-cloak
                      @drop.prevent="onFileChange"
                      @dragover.prevent
                    >
                      <b-avatar
                        :src="
                          tradie.avatar
                            ? tradie.avatar.thumb_url || tradie.avatar.url
                            : null
                        "
                        :button="canManageTradie"
                        @click="canManageTradie && (showImageCropper = true)"
                        class="ml-0 mr-auto"
                        style="width: 180px; height: 240px"
                        rounded
                        :badge="canManageTradie"
                        badge-top
                        badge-offset="0.5rem"
                        badge-variant="transparent"
                      >
                        <template v-if="canManageTradie" #badge>
                          <b-icon
                            icon="pencil-square"
                            scale="1.5"
                            class="text-light"
                          />
                        </template>
                      </b-avatar>
                    </div>

                    <b-row no-gutters class="profile-details-grid">
                      <!-- Profile -->
                      <b-col cols="12" class="mb-0">
                        <h4>Profile:</h4>
                      </b-col>
                      <b-col cols="12" md="4" lg="3" class="mb-3">
                        <span class="font-weight-bold">Name:</span>
                        {{ tradie.full_name }}
                      </b-col>
                      <b-col cols="12" md="4" lg="3" class="mb-3">
                        <span class="font-weight-bold">Email:</span>
                        {{ tradie.email }}
                      </b-col>
                      <b-col cols="12" md="4" lg="3" class="mb-3">
                        <span class="font-weight-bold">Trade:</span>
                        {{ tradie.trade }}
                      </b-col>
                      <template v-if="tradie.consent_given">
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Date of Birth:</span>
                          {{ dateFormat(tradie.details.birthdate) }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Gender:</span>
                          {{ tradie.details.gender || 'Prefer not to say' }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Phone Number:</span>
                          {{
                            formatPhoneWithDialCode(tradie.details.phone_number)
                          }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">USI:</span>
                          {{ tradie.details.usi }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Trainee? </span>
                          {{ tradie.details.is_trainee ? 'Yes' : 'No' }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Aboriginal? </span>
                          {{ tradie.details.is_aboriginal ? 'Yes' : 'No' }}
                        </b-col>
                        <b-col cols="12" md="4" lg="3" class="mb-3">
                          <span class="font-weight-bold">Address:</span><br />
                          {{ tradie.details.address }}
                        </b-col>
                        <b-col cols="12" md="4" lg="3" class="mb-3">
                          <span class="font-weight-bold">
                            Medical Conditions:
                          </span>
                          <br />
                          {{ tradie.details.medical_conditions }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">PPE Size:</span><br />
                          {{ tradie.details.ppe_size || '-' }}
                        </b-col>

                        <!-- Emergency Contact -->
                        <b-col cols="12" class="mt-2 mb-0">
                          <h4>Emergency Contact:</h4>
                        </b-col>
                        <b-col cols="12" md="4" lg="3" class="mb-3">
                          <span class="font-weight-bold">Name:</span>
                          {{
                            tradie.emergency_contact.emergency_contact_full_name
                          }}
                        </b-col>
                        <b-col cols="12" md="4" lg="3" class="mb-3">
                          <span class="font-weight-bold">Email:</span>
                          {{ tradie.emergency_contact.emergency_contact_email }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Phone Number:</span>
                          {{
                            formatPhoneWithDialCode(
                              tradie.emergency_contact
                                .emergency_contact_phone_number,
                            )
                          }}
                        </b-col>
                        <b-col
                          cols="12"
                          md="4"
                          lg="3"
                          class="mb-3 profile-col-half"
                        >
                          <span class="font-weight-bold">Relationship:</span>
                          {{
                            tradie.emergency_contact
                              .emergency_contact_relationship
                          }}
                        </b-col>
                      </template>
                      <b-col v-else cols="12">
                        <div class="consent-hidden-banner">
                          <b-icon-shield-lock
                            class="consent-hidden-banner__icon"
                          />
                          <div>
                            <p class="mb-1">
                              Personal details hidden until the tradie accepts
                              the privacy consent.
                            </p>
                            <p class="mb-0">
                              Please contact the worker to accept the privacy
                              consent through the app.
                            </p>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </b-container>
              </b-card>
            </b-container>
          </b-row>

          <!-- ROW - Files -->
          <b-row>
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <b-container fluid>
                  <b-row
                    align-v="center"
                    align-h="between"
                    no-gutters
                    class="section-header-row"
                  >
                    <b-col cols="auto">
                      <h3 class="font-weight-bold">Files:</h3>
                    </b-col>
                    <b-col
                      v-if="canViewTradieFiles && canCreateTradieFile"
                      cols="auto"
                    >
                      <b-button
                        variant="primary"
                        class="pl-2 pr-3"
                        :to="{ name: 'templates.list-page' }"
                        style="display: table-cell"
                      >
                        <b-icon-plus
                          font-scale="1.5"
                          class="align-middle"
                        ></b-icon-plus>
                        <span class="align-middle">New File</span>
                      </b-button>
                    </b-col>
                  </b-row>
                  <b-row v-if="canViewTradieFiles">
                    <b-col cols="12">
                      <div class="tradie-files-notice">
                        <span
                          class="tradie-files-notice__icon"
                          aria-hidden="true"
                        >
                          <b-icon-exclamation-triangle-fill />
                        </span>
                        <div class="tradie-files-notice__body">
                          <span class="tradie-files-notice__title d-block mb-2">
                            Important Update on Uploading Files
                          </span>
                          <p class="mb-2">
                            <strong>Private:</strong>
                            Only visible to your company and not shared with the
                            Tradie or other linked companies. For company-only
                            documents, use Private or upload them via the
                            <strong>Company Files</strong>
                            section.
                          </p>
                          <p class="mb-0">
                            <strong>Public:</strong>
                            Visible to the Tradie and may be visible to other
                            companies linked to that Tradie.
                          </p>
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                  <p v-if="!canViewTradieFiles" class="text-muted py-3 mb-0">
                    You don't have permission to view this tradie's files.
                  </p>
                  <template v-else>
                    <!-- Mobile: stacked file cards (hidden on md+) -->
                    <div class="d-md-none mobile-file-list">
                      <div
                        v-for="item in files"
                        :key="item.id"
                        class="mobile-file-item"
                        :class="mobileFileAttentionClass(item)"
                        @click="viewFile(item)"
                      >
                        <div class="d-flex align-items-start">
                          <div class="flex-grow-1" style="min-width: 0">
                            <div class="mobile-file__name">
                              {{ item.display_name || item.name || '-' }}
                            </div>
                            <div class="mobile-file__meta">
                              <span>
                                <span class="mobile-file__label">Issued</span>
                                {{ item.date_issued || '-' }}
                              </span>
                              <span>
                                <span class="mobile-file__label">Expiry</span>
                                {{ item.date_expiry || '-' }}
                              </span>
                              <span>
                                <span class="mobile-file__label">
                                  Visibility
                                </span>
                                {{
                                  formatTradieViewVisibility(
                                    item.visibility,
                                    'visibility',
                                    item,
                                  )
                                }}
                              </span>
                            </div>
                          </div>
                          <div
                            class="mobile-file__status"
                            v-b-tooltip.hover="getStatusCellTooltip(item)"
                          >
                            <b-badge
                              v-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expired'
                              "
                              pill
                              variant="danger"
                              class="tradie-file-status-badge"
                              >Expired</b-badge
                            >
                            <b-badge
                              v-else-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expiring_soon'
                              "
                              pill
                              variant="warning"
                              class="tradie-file-status-badge tradie-file-status-badge--soon"
                              >Expiring soon</b-badge
                            >
                            <span v-else class="mobile-file__status-text">{{
                              formatTradieFilesStatusDisplay(
                                item && item.status,
                              )
                            }}</span>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="!files || !files.length"
                        class="text-center py-4 text-muted"
                      >
                        No files found.
                      </div>
                    </div>

                    <!-- Desktop: full table (hidden below md) -->
                    <div class="d-none d-md-block">
                      <b-table
                        id="my-table"
                        responsive
                        hover
                        show-empty
                        :fields="fields"
                        :items="files"
                        :tbody-tr-class="profileFileAttentionRowClass"
                        @row-clicked="viewFile"
                      >
                        <template #table-busy>
                          <div class="text-center text-primary my-2">
                            <digger-loader />
                          </div>
                        </template>

                        <template #cell(status)="{ item }">
                          <span
                            class="tradie-file-status-cell"
                            v-b-tooltip.hover="getStatusCellTooltip(item)"
                          >
                            <b-badge
                              v-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expired'
                              "
                              pill
                              variant="danger"
                              class="tradie-file-status-badge"
                              >Expired</b-badge
                            >
                            <b-badge
                              v-else-if="
                                getTradieProfileFileAttentionLevel(item) ===
                                'expiring_soon'
                              "
                              pill
                              variant="warning"
                              class="tradie-file-status-badge tradie-file-status-badge--soon"
                              >Expiring soon</b-badge
                            >
                            <template v-else>{{
                              formatTradieFilesStatusDisplay(
                                item && item.status,
                              )
                            }}</template>
                          </span>
                        </template>
                      </b-table>
                    </div>
                  </template>
                </b-container>
              </b-card>
            </b-container>

            <!-- Modal - View File -->
            <ViewFileModal
              :showModal="isViewingFile"
              :fileId="viewFileId"
              @close="onViewFileClose"
              @error="onViewFileError"
              @deleted="onViewFileDeleted"
              @marked-na="onViewFileMarkedNa"
            />
          </b-row>

          <!-- ROW - Requested Files -->
          <b-row>
            <b-container fluid>
              <b-card class="mt-4 file-details">
                <b-container fluid>
                  <b-row
                    align-v="center"
                    align-h="between"
                    no-gutters
                    class="section-header-row"
                  >
                    <b-col cols="auto">
                      <h3 class="font-weight-bold">Requested Files:</h3>
                    </b-col>
                    <b-col cols="auto">
                      <b-button
                        v-if="canViewTradieFiles && canCreateTradieFile"
                        variant="primary"
                        class="pl-2 pr-3"
                        @click="selectingTemplate(true)"
                      >
                        <b-icon-plus
                          font-scale="1.5"
                          class="align-middle"
                        ></b-icon-plus>
                        <span class="align-middle">Request New File</span>
                      </b-button>
                    </b-col>
                  </b-row>
                  <p v-if="!canViewTradieFiles" class="text-muted py-3 mb-0">
                    You don't have permission to view this tradie's files.
                  </p>
                  <project-tradie-requested-file-list
                    v-else
                    :requests="requestedFiles"
                    @loading="isLoading"
                    @onRequestDeleted="onFileRequestDeleted"
                    class="mt-4"
                  />
                </b-container>
              </b-card>
            </b-container>
          </b-row>
        </b-container>

        <select-template-modal
          :showModal="isSelectingTemplate"
          @closeModal="isSelectingTemplate = false"
          @selected="onTemplateSelected"
        />

        <tradie-request-file-modal
          v-if="tradie"
          :emails="[tradie.email]"
          :showModal="isRequestingFile"
          :template="requestFileTemplate"
          @sendRequestResponse="onFileRequested"
          @closeModal="requestingFile(false)"
        />

        <existing-file-modal
          :show-modal="showExistingFileModal"
          :files="existingFiles"
          subject="This tradie already has"
          :template-label="requestFileTemplate.label || 'file'"
          show-company-approval
          view-only
          @close="closeExistingFileModal"
          @createNew="onRequestAnotherFile"
          @view="onViewExistingFile"
        />

        <b-alert
          class="custom-alert"
          :show="dismissCountDown"
          :class="{
            'bg-danger border-danger': alertType === 'danger',
          }"
          dismissible
          fade
          :variant="alertType"
          @dismiss-count-down="countDownChanged"
        >
          {{ alertMsg }}
        </b-alert>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ProjectTradieRequestedFileList from '@/components/lists/ProjectTradieRequestedFileList'
import SelectTemplateModal from '@/components/modals/SelectTemplateModal'
import TradieRequestFileModal from '@/components/modals/TradieRequestFileModal'
import ExistingFileModal from '@/components/modals/ExistingFileModal'
import { matchFilesForTemplate } from '@/utils/fileMatch'
import ViewFileModal from '@/components/modals/ViewFileModal.vue'
import ImageCropper from '@/components/modals/ImageCropper'
import { formatFileVisibilityDisplay } from '@/utils/fileLabels'
import {
  formatTradieFileStatusColumnDisplay,
  formatTradieFilesStatusDisplay,
  getTradieProfileFileAttentionLevel,
  isTradieFileMarkedNa,
  formatStatusSetDate,
} from '@/utils/fileAttention'
import { formatPhoneWithDialCode } from '@/utils/phonePolicy'
import { parseServerDate } from '@/utils/dateHelper'
import { clearTradieUploadContextUnlessInFlow } from '@/utils/tradieUploadContext'

// Routes you can only reach by drilling *into* this profile: viewing/editing
// a tradie's file, the "New File" template picker, the request-file flow.
// Coming back from one of these is a step back *out*, not a fresh origin —
// recording it as previousRoute made "Back to Tradies" point at the file
// view, whose own Back returns here, so the two buttons ping-ponged forever.
// Mirrors FILE_VIEW_SUBROUTES in ViewFilePage.vue.
//
// candidates.view-candidate belongs here too, for a different reason: a
// candidate who's already on the team 409s out of CandidateProfileController
// ::show(), and ViewCandidate.vue's catch handler *replaces* that route with
// this one (see ViewCandidate.vue's fetchCandidate()). That replace still
// runs this guard with `from` pointing at candidates.view-candidate, which
// isn't a real page the user chose to visit — it's the page that just bounced
// them here. Recording it as previousRoute sent "Back to Tradies" straight
// back into the same candidate profile, which 409s again and replaces back
// to this same tradie profile: an infinite bounce the user couldn't escape.
// That redirect is tracked separately below (arrivedFromCandidateRedirect)
// so the button can say — and actually do — "Back to Candidates" instead.
const PROFILE_DRILLDOWN_ROUTES = [
  'file.view-page',
  'file.edit-page',
  'file.new-page',
  'file.success-page',
  'templates.list-page',
  'template.form-page',
  'request.request-file-new-page',
  'request.request-file-template-form-page',
  'candidates.view-candidate',
]

// Module-scoped so the recorded origin (and the candidate-redirect flag
// below) survive this component being destroyed while the user dips into a
// file view/edit and comes back.
let lastTradieProfileOrigin = null
let lastArrivedFromCandidateRedirect = false

export default {
  components: {
    ProjectTradieRequestedFileList,
    SelectTemplateModal,
    TradieRequestFileModal,
    ExistingFileModal,
    ViewFileModal,
    ImageCropper,
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const cameFromName = from && from.name
      const isFreshOrigin =
        cameFromName &&
        cameFromName !== to.name &&
        !PROFILE_DRILLDOWN_ROUTES.includes(cameFromName)

      if (isFreshOrigin) {
        lastTradieProfileOrigin = {
          name: from.name,
          params: from.params || {},
          query: from.query || {},
        }
        lastArrivedFromCandidateRedirect = false
      } else if (cameFromName === 'candidates.view-candidate') {
        // Fresh entry via the already-hired-candidate 409 redirect (not a
        // drill-down round trip, since lastArrivedFromCandidateRedirect only
        // gets reset above on a genuine fresh origin) — remember it so the
        // back button targets Candidates instead of Tradies.
        lastArrivedFromCandidateRedirect = true
      }

      vm.previousRoute = lastTradieProfileOrigin
      vm.arrivedFromCandidateRedirect = lastArrivedFromCandidateRedirect
    })
  },

  data() {
    return {
      // Ids
      tradieId: this.$route.params.tradieId,

      // Where the user came from (used by "Back to Tradies" so navigating
      // back from the home dashboard returns to the home dashboard).
      previousRoute: null,

      // True when this profile was only reached because the candidate
      // profile the user actually clicked into 409'd (already hired) and
      // redirected here — see PROFILE_DRILLDOWN_ROUTES above. Set by
      // beforeRouteEnter. Flips the back button to "Back to Candidates".
      arrivedFromCandidateRedirect: false,

      // Status Booleans
      isLoading: false,
      isSelectingTrades: false,
      isLoadingTrades: false,
      isSelectingTemplate: false,
      isRequestingFile: false,
      showExistingFileModal: false,
      existingFiles: [],
      isViewingFile: false,
      showImageCropper: false,

      // Variables
      searchQuery: '',
      selectedTradeIds: [],
      requestFileTemplate: {
        id: null,
        name: '',
      },
      viewFileId: 0,
      dismissCountDown: 0,

      alertType: 'primary',
      alertMsg: '',
    }
  },

  computed: {
    // Set while the tradie is inside their account-deletion notice period.
    accountDeletionDate() {
      const at = this.tradie && this.tradie.deletion_scheduled_at
      const d = at ? parseServerDate(at) : null
      return d
        ? d.toLocaleDateString('en-AU', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : ''
    },
    ...mapGetters('company', ['tradie', 'files', 'requestedFiles']),

    breadcrumbs() {
      return [
        {
          text: 'Tradies',
          to: '/tradies',
        },
        {
          text: this.tradie ? this.tradie.full_name : '',
        },
      ]
    },

    fields() {
      // Desktop-only table (md+). Below md the files render as stacked cards
      // (see the .d-md-none block in the template), so no column hiding here.
      return [
        {
          key: 'name',
          label: 'File Name',
          class: 'w-25',
          sortable: true,
          // API returns the plain file name in `name` and the
          // course/training-suffixed version separately as `display_name`
          // (see File::displayName()) - the project-scoped tradie table
          // already shows the suffixed form, this one was missing it.
          formatter: (_value, _key, item) => item.display_name || item.name,
        },
        {
          key: 'date_issued',
          label: 'Issued Date',
          sortable: true,
        },
        {
          key: 'date_expiry',
          label: 'Expiry Date',
          sortable: true,
        },
        {
          key: 'visibility',
          label: 'Visibility',
          sortable: true,
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          formatter: this.formatTradieViewVisibility,
        },
        {
          key: 'status',
          label: 'Tradie Pass Verification',
          sortable: true,
          thClass: 'text-nowrap',
        },
      ]
    },

    ...mapGetters('auth', ['user', 'hasPermission']),

    // RBAC gates for tradie files (business-admin-only screen).
    canManageTradie() {
      return this.hasPermission('tradies.profile.view')
    },
    canViewTradieFiles() {
      return this.hasPermission('tradies.files.view')
    },
    canCreateTradieFile() {
      // No new or requested files for a tradie in their account-deletion window.
      if (this.tradie && this.tradie.deletion_scheduled_at) return false
      return this.hasPermission('tradies.files.create')
    },
  },

  beforeRouteLeave(to, from, next) {
    clearTradieUploadContextUnlessInFlow(to)
    next()
  },

  methods: {
    formatTradieFileStatusColumnDisplay,
    formatTradieFilesStatusDisplay,
    getTradieProfileFileAttentionLevel,
    isTradieFileMarkedNa,
    formatPhoneWithDialCode,

    profileFileAttentionRowClass(item, type) {
      if (!item || type !== 'row') return 'cursor-pointer'
      const level = getTradieProfileFileAttentionLevel(item)
      if (level === 'expired')
        return 'cursor-pointer bg-alert tradie-file-row-attention'
      if (level === 'expiring_soon')
        return 'cursor-pointer bg-highlight tradie-file-row-attention'
      return 'cursor-pointer'
    },

    // Attention background for the compact mobile file cards, mirroring the
    // desktop table row highlighting.
    mobileFileAttentionClass(item) {
      const level = getTradieProfileFileAttentionLevel(item)
      if (level === 'expired') return 'mobile-file-item--expired'
      if (level === 'expiring_soon') return 'mobile-file-item--soon'
      return ''
    },

    /**
     * BA tradie profile files table: friendlier labels than rest of app (ViewTradie only).
     */
    formatTradieViewVisibility(_value, _key, item) {
      const base = formatFileVisibilityDisplay(item)
      if (base === 'Public') return 'Shared'
      if (base === 'Private') return 'Company Only'
      return base
    },

    ...mapActions('company', {
      getTradie: 'getTradie',
    }),

    ...mapActions({
      changeTradieAvatar: 'auth/changeTradieAvatar',
    }),

    async gotoTradies() {
      if (this.arrivedFromCandidateRedirect) {
        this.$router.push({ name: 'candidates.list-page' })
        return
      }
      if (this.previousRoute && this.previousRoute.name) {
        this.$router.push(this.previousRoute)
        return
      }
      this.$router.push({
        name: 'tradies.list-page',
      })
    },

    onFileChange(e) {
      this.showImageCropper = true
      this.$root.$emit('fileChange', e)
    },

    async uploadProfilePhoto(image) {
      let data = new FormData()
      data.append('avatar', image)
      data.append('tradie_id', this.tradieId)

      await this.changeTradieAvatar(data)
        .then((res) => {
          this.showImageCropper = false
          this.fetchTradie()
        })
        .catch((err) => this.handleError(err))
        .finally(() => this.$root.$emit('uploadComplete'))
    },

    async fetchTradie() {
      this.isLoading = true
      try {
        await this.getTradie({
          tradieId: this.tradieId,
        })
        this.formatFiles()
      } catch (err) {
        if (err.response || err.status === 403) {
          this.showToast('', 'Assign a token to view this tradie.', 'danger')
          this.$router.push('/tradies')
        }
      } finally {
        this.isLoading = false
      }
      // .catch((err) => this.handleError(err))
      // .finally(() => (this.isLoading = false))
    },

    viewFile(item, i) {
      if (!this.canViewTradieFiles) return
      this.viewFileId = item.id
      this.isViewingFile = true
    },

    onViewFileClose() {
      this.isViewingFile = false
    },

    onViewFileError(err) {
      this.handleError(err)
    },

    onViewFileMarkedNa(fileId) {
      this.fetchTradie()
    },

    onViewFileDeleted(fileId) {
      const i = this.files.findIndex((x) => x.id === fileId)
      this.files.splice(i, 1)
      this.onViewFileClose()
    },

    async selectingTemplate(status) {
      if (status && !this.canCreateTradieFile) return
      this.isSelectingTemplate = status
    },

    async onTemplateSelected(template) {
      this.requestFileTemplate = template

      // `files` is what this viewer can see of the tradie's files (the
      // tradie's public files plus this company's own, never another
      // business's private ones). Warn if one of this type is already there,
      // but let them request another regardless. Archived files don't count.
      const matches = matchFilesForTemplate(
        (this.files || []).filter(
          (f) => f && f.company_approval !== 'not_applicable',
        ),
        template,
      )
      if (matches.length) {
        this.existingFiles = matches
        this.showExistingFileModal = true
        return
      }

      this.requestingFile(true)
    },

    closeExistingFileModal() {
      this.showExistingFileModal = false
      this.existingFiles = []
    },

    onRequestAnotherFile() {
      this.closeExistingFileModal()
      this.requestingFile(true)
    },

    onViewExistingFile(file) {
      this.closeExistingFileModal()
      if (file) this.viewFile(file)
    },

    requestingFile(status) {
      this.isRequestingFile = status
    },

    onFileRequested(res) {
      if (res.success) {
        this.alertMsg = 'File request shared successfully'
        this.dismissCountDown = 15
      }

      this.fetchTradie()
      this.requestingFile(false)
    },
    getStatusCellTooltip(item) {
      if (!item) return { title: '', disabled: true }
      const statusLabel = this.tradieFileStatusLabel(item)
      if (!statusLabel) return { title: '', disabled: true }
      let reason = ''
      const raw = item.status_reason
      if (raw && raw !== '-') {
        const trimmed = String(raw).trim()
        if (trimmed) reason = trimmed
      }
      const setDate = formatStatusSetDate(item.status_updated_at)
      let title = reason ? `${statusLabel}: ${reason}` : statusLabel
      if (setDate) title += ` - ${setDate}`
      return { title, disabled: false }
    },
    tradieFileStatusLabel(item) {
      const level = getTradieProfileFileAttentionLevel(item)
      if (level === 'expired') return 'Expired'
      if (level === 'expiring_soon') return 'Expiring soon'
      const raw = formatTradieFilesStatusDisplay(item && item.status)
      if (raw == null || raw === '') return ''
      return String(raw).replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatFileRowReasons(list) {
      ;(list || []).forEach((file) => {
        if (file.status === 'expired') {
          this.$set(file, 'status_reason', '-')
        }
        if (file.status === 'pending approval') {
          this.$set(file, 'status_reason', '')
        }
      })
    },
    formatFiles() {
      this.formatFileRowReasons(this.files)
    },
    onFileRequestDeleted(fileRequestId) {
      const i = this.requestedFiles.findIndex(
        (x) => x.file_request_id === fileRequestId,
      )
      this.requestedFiles.splice(i, 1)
    },

    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },

    handleError(err) {
      this.showToast(
        'Oops.. Something went wrong!',
        err.response.data.message ? err.response.data.message : err.message,
        'danger',
      )
    },

    dateFormat(dateString) {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
  },

  async mounted() {
    await this.fetchTradie()
    localStorage.setItem('project_id', this.projectId)
    localStorage.setItem('tradie_id', this.tradieId)
  },
}
</script>

<style lang="scss" scoped>
$targetHeight: 52.5px;

.consent-hidden-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background-color: #fff8ec;
  border: 1px solid #f5dfae;
  border-left: 4px solid #e2a53a;
  border-radius: 6px;
  padding: 0.85rem 1rem;
  color: #7a5a1e;
  font-size: 0.875rem;

  &__icon {
    flex-shrink: 0;
    margin-top: 0.15rem;
    font-size: 1.1rem;
    color: #e2a53a;
  }

  p {
    color: inherit;
  }
}

/deep/ .b-form-tags-list {
  height: calc(#{$targetHeight} - 16px);
}

/deep/ .b-form-tags-input {
  height: calc(#{$targetHeight} - 16px - 6px);
}

/deep/ .cursor-pointer {
  cursor: pointer;
}

/deep/ .cursor-pointer td {
  vertical-align: middle;
}

.tradie-files-notice {
  position: relative;
  width: 100%;
  margin-top: 1rem;
  padding: 1rem 1.25rem 1rem 2.75rem;
  background: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 8px;
  color: #383d41;

  &__icon {
    position: absolute;
    left: 1.25rem;
    top: 1rem;
    color: #ffc107;
    font-size: 1.1rem;
    line-height: 1;
  }

  &__title {
    font-weight: 700;
    color: #383d41;
    font-size: 0.9375rem;
  }

  &__body {
    line-height: 1.5;
    font-size: 0.95rem;

    strong {
      font-weight: 600;
    }
  }
}

.tradie-file-status-cell {
  display: inline-block;
}

.tradie-file-status-badge {
  font-weight: 600;
}

.tradie-file-status-badge--soon {
  background-color: #fff3cd !important;
  color: #856404 !important;
  border: 1px solid #ffc107;
}

/deep/ tr.tradie-file-row-attention.bg-alert,
/deep/ tr.tradie-file-row-attention.bg-alert:hover {
  background-color: #ffeaea !important;
}

/deep/ tr.tradie-file-row-attention.bg-highlight {
  background-color: #fff8e1 !important;
}

/* ---------------------------------------------------------------------------
 * Small-screen responsiveness (down to 320px).
 *
 * All rules live inside max-width media queries so the desktop/laptop layout
 * is left byte-for-byte identical. The layout gets progressively more compact
 * as the viewport shrinks: spacing tightens first (<=768px), then structural
 * elements stack (<=576px), then sizes shrink further on the smallest phones.
 * ------------------------------------------------------------------------- */

/* Tablet / large phone: trim the oversized vertical spacing that otherwise
 * pushes content far down the page on shorter viewports. */
@media (max-width: 767.98px) {
  .tradie-profile-view {
    padding-top: 2rem !important;
    padding-bottom: 2rem !important;
  }

  /deep/ .breadcrumb {
    padding-top: 2rem !important;
    margin-top: 2rem !important;
  }

  .profile-detail-flex {
    gap: 1.5rem !important;
  }
}

/* Phones: stack the previously side-by-side sections and reclaim padding. */
@media (max-width: 575.98px) {
  .tradie-profile-view {
    padding-top: 1.25rem !important;
    padding-bottom: 1.25rem !important;
  }

  /deep/ .breadcrumb {
    padding-top: 1rem !important;
    margin-top: 1rem !important;
    font-size: 0.85rem;
  }

  /* Title + "Back to Tradies" stack vertically instead of splitting the row. */
  .profile-header-row {
    flex-direction: column;

    > .col,
    > [class*='col-'] {
      flex: 0 0 100%;
      max-width: 100%;
      text-align: left !important;
    }

    h2 {
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
    }

    .btn {
      max-width: 100% !important;
      margin-top: 0.25rem;
    }
  }

  /* Avatar sits above the profile detail grid rather than beside it. */
  .profile-detail-flex {
    flex-direction: column;
    gap: 1rem !important;
    margin-top: 1rem !important;
  }

  /* Reclaim horizontal space eaten by default card padding. */
  /deep/ .file-details > .card-body {
    padding: 1rem;
  }

  /* Profile details: lay the fields out as a 2-column grid so the narrow
   * screen's width is actually used. Long values and section headers span
   * the full width; short values (dates, toggles, phone, etc.) pair up. */
  .profile-details-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1rem;
    row-gap: 0.65rem;

    /* Default: every field takes the full row. */
    > * {
      grid-column: 1 / -1;
      margin-bottom: 0 !important;
      font-size: 0.85rem;
      line-height: 1.35;
    }

    /* Short fields sit two-per-row. */
    > .profile-col-half {
      grid-column: span 1;
    }

    /* Turn "Label: value" into a stacked label-over-value pair for a cleaner,
     * less cramped read. */
    .font-weight-bold {
      display: block;
      font-size: 0.66rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #8b95a3;
      margin-bottom: 1px;
    }

    /* The stacked label already provides the line break. */
    br {
      display: none;
    }

    h4 {
      font-size: 0.95rem;
      margin-top: 0.35rem;
      margin-bottom: 0.15rem;
    }
  }

  /* Section title + action button wrap with breathing room. */
  .section-header-row {
    row-gap: 0.75rem;

    /* The global .btn is a fixed 56px tall with wide padding, which looks
     * oversized on phones. Slim it down into a compact action button. */
    .btn {
      height: auto;
      padding: 0.4rem 0.8rem;
      font-size: 0.85rem;
      border-radius: 6px;

      /deep/ svg,
      .b-icon {
        font-size: 1.1rem;
      }
    }
  }

  h3 {
    font-size: 1.25rem;
  }
  h4 {
    font-size: 1.1rem;
  }

  /* Compact the upload-guidance notice so it doesn't dominate the screen. */
  .tradie-files-notice {
    margin-top: 0.75rem;
    padding: 0.75rem 0.85rem 0.75rem 2.15rem;
    border-radius: 6px;

    &__icon {
      left: 0.8rem;
      top: 0.75rem;
      font-size: 0.95rem;
    }

    &__title {
      font-size: 0.85rem;
      margin-bottom: 0.35rem !important;
    }

    &__body {
      font-size: 0.8rem;
      line-height: 1.45;

      p {
        margin-bottom: 0.45rem !important;
      }
    }
  }
}

/* Mobile files list: below md the files table is replaced by compact stacked
 * cards (one file per card) so nothing scrolls sideways. Matches the styling
 * of the mobile tradie list used elsewhere in the app. */
.mobile-file-list {
  border-top: 1px solid #e1e6ea;
}

.mobile-file-item {
  padding: 0.65rem 0.25rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
  cursor: pointer;
}

.mobile-file-item--expired {
  background-color: #ffeaea;
}

.mobile-file-item--soon {
  background-color: #fff8e1;
}

.mobile-file__name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #364052;
  line-height: 1.3;
  word-break: break-word;
}

.mobile-file__meta {
  font-size: 0.75rem;
  color: #8b95a3;
  margin-top: 0.2rem;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
}

.mobile-file__label {
  font-weight: 600;
  color: #6c7581;
  margin-right: 0.15rem;
}

.mobile-file__status {
  flex-shrink: 0;
  margin-left: 0.5rem;
  text-align: right;
}

.mobile-file__status-text {
  font-size: 0.72rem;
  color: #6c7581;
  text-transform: capitalize;
}

/* Smallest phones (<=~360px): shrink the avatar so it never crowds the edges. */
@media (max-width: 359.98px) {
  .profile-detail-flex /deep/ .b-avatar {
    width: 140px !important;
    height: 187px !important;
  }
}
</style>
